import type { MotivoLectura, ResultadoLectura } from '../api/ops/pases';
import type { NombreIcono } from '../componentes/iconos';

/**
 * Textos de la Puerta en la tablet (6.1 y 6.2), tal cual del diseño. Cada motivo de `ScanReason` en palabras (G214):
 * verde, ámbar y rojo siempre con icono y palabra; la fila del deshacer, sin color (G416, G421).
 */

export interface DatosLectura {
    nombreNegocio: string;
    /** El pase de la respuesta (o de la copia sin conexión). */
    tipo?: string | null;
    lista?: string | null;
    zona?: string | null;
    desde?: string | null;
    hasta?: string | null;
    /** `previous_arrival`: hora, tablet y quién. */
    antes?: { hora: string; tablet: string; quien: string } | null;
    ahora: string;
    /** Hora de la última actualización de la copia (sin conexión). */
    copia?: string | null;
}

export const COLOR: Readonly<Record<ResultadoLectura, { icono: NombreIcono; palabra: string; clase: string }>> = {
    green: { icono: 'check', palabra: 'Puede pasar', clase: 'verde' },
    amber: { icono: 'triangle-alert', palabra: 'Revisa', clase: 'ambar' },
    red: { icono: 'x', palabra: 'No puede pasar', clase: 'rojo' },
};

const unir = (...x: (string | null | undefined)[]) => x.filter((v): v is string => v != null && v !== '').join(' · ');

/** Título y explicación del resultado. */
export function explicar(motivo: MotivoLectura, d: DatosLectura): { titulo: string; texto: string } {
    switch (motivo) {
        case 'ok':
            return { titulo: 'Pase válido', texto: unir(d.tipo, d.lista, d.zona) };
        case 'reentry':
            return { titulo: 'Vuelve a entrar', texto: unir(d.tipo, 'Este pase admite varias entradas.') };
        case 'admitted_override':
            return { titulo: 'Admitido igualmente', texto: 'Queda en Auditoría con tu nombre y el motivo.' };
        case 'already_inside':
            return {
                titulo: 'Ya entró',
                texto: d.antes == null ? 'Un pase vale para una sola entrada.' : `Entró a las ${d.antes.hora} por ${d.antes.tablet} (lo validó ${d.antes.quien}). Un pase vale para una sola entrada.`,
            };
        case 'concurrent_arrival':
            return { titulo: 'Otra tablet lo acaba de registrar', texto: d.antes == null ? 'Si es la misma persona, ya está dentro.' : `${d.antes.tablet} (${d.antes.quien}) a las ${d.antes.hora}. Si es la misma persona, ya está dentro.` };
        case 'outside_window':
            return { titulo: 'Fuera de horario', texto: d.desde == null || d.hasta == null ? `Son las ${d.ahora}.` : `Su pase vale de ${d.desde} a ${d.hasta} y son las ${d.ahora}.` };
        case 'list_cutoff':
            return { titulo: 'Pasada la hora límite de la lista', texto: `${d.lista == null ? 'La lista' : `La ${d.lista}`} ya ha cerrado y son las ${d.ahora}.` };
        case 'issued_after_package':
            return {
                titulo: 'Pase auténtico, posterior a la copia',
                texto: `Se emitió después de la última actualización${d.copia == null ? '' : ` (${d.copia})`}. La firma es buena, pero sin conexión no podemos saber si sigue activo.`,
            };
        case 'not_found':
            return { titulo: 'Pase no encontrado', texto: `Este código no es de ningún pase de ${d.nombreNegocio}.` };
        case 'bad_signature':
            return { titulo: 'QR no válido', texto: 'La firma no cuadra: el QR está alterado o no lo ha emitido este negocio.' };
        case 'other_business':
            return { titulo: 'Pase de otro negocio', texto: `Es un pase auténtico, pero no es de ${d.nombreNegocio}. Aquí no da acceso.` };
        case 'voided':
            return { titulo: 'Pase anulado', texto: 'Este pase se anuló (pedido cancelado o invitado retirado de la lista). No da acceso.' };
        case 'expired':
            return { titulo: 'Pase caducado', texto: 'Su acceso terminó sin llegada.' };
        case 'key_revoked':
            return { titulo: 'QR con una clave revocada', texto: 'Se firmó con una clave que el negocio ha retirado por seguridad. Este QR ya no da acceso.' };
        case 'undone':
            return { titulo: 'Llegada deshecha', texto: '' };
    }
}

/** Línea corta de «Últimas lecturas»: lo verde dice el tipo; lo demás, su motivo. */
export function lineaCorta(motivo: MotivoLectura, d: DatosLectura): string {
    switch (motivo) {
        case 'ok':
        case 'reentry':
            return d.tipo ?? 'Pase válido';
        case 'outside_window':
            return d.desde == null ? 'Fuera de horario' : `Fuera de horario · vale de ${d.desde} a ${d.hasta ?? ''}`;
        case 'already_inside':
            return d.antes == null ? 'Ya entró' : `Ya entró a las ${d.antes.hora} · ${d.antes.tablet}`;
        case 'concurrent_arrival':
            return d.antes == null ? 'Otra tablet' : `Otra tablet: ${d.antes.tablet} a las ${d.antes.hora}`;
        case 'issued_after_package':
            return d.copia == null ? 'Emitido después de la copia' : `Emitido después de la copia de las ${d.copia}`;
        default:
            return explicar(motivo, d).titulo;
    }
}

export const NO_ES_TUYA = { titulo: 'No es una cita tuya', texto: 'Solo puedes registrar la llegada de tus citas. Avisa a recepción.' };

export const PRESETS_ADMITIR = ['Lo autoriza el encargado', 'Viene con su RRPP', 'Llega con retraso justificado'] as const;

export const MOTIVOS_EVALUACION = [
    ['staff_behavior', 'Comportamiento con el personal'],
    ['damage', 'Daños'],
    ['non_payment', 'Impago'],
    ['venue_rules', 'Normas del local'],
    ['harassment', 'Acoso a otros clientes'],
] as const;

export const NIVELES_EVALUACION = [
    ['green', 'Verde', 'Quita la vigente', 'check'],
    ['amber', 'Ámbar', '6 meses', 'triangle-alert'],
    ['red', 'Rojo', '12 meses', 'x'],
] as const;

export const T_PUERTA = {
    conConexion: 'Con conexión',
    sinConexion: 'Sin conexión',
    cambiar: 'Cambiar de persona',
    apunta: 'Apunta al QR del pase',
    camaraCerrada: 'Cámara cerrada',
    camaraCerradaTexto: 'El lector de pistola sigue escuchando: lee el QR y el resultado sale aquí. También puedes buscar por nombre o código.',
    abrirCamara: 'Abrir la cámara',
    sinLectorCamara: 'Este navegador no sabe leer QR con la cámara. El lector de pistola y la búsqueda siguen funcionando.',
    buscar: 'Buscar por nombre o código',
    buscarProfesional: 'Buscar tu cita por nombre',
    sinResultados: 'No hay ningún pase con ese nombre o código.',
    dentro: 'Dentro',
    ultimas: 'Últimas lecturas',
    rechazar: 'Rechazar',
    admitir: 'Admitir igualmente',
    soloEncargado: 'Solo un encargado puede admitirlo igualmente.',
    // G783: lo decide un encargado con su PIN en esta tablet (no dibujado: propuesto).
    pedirEncargado: 'Que lo admita un encargado',
    admitirA: (nombre: string) => `Admitir igualmente a ${nombre}`,
    admitirSub: (titulo: string) => `${titulo}. Queda en Auditoría con tu nombre y el motivo.`,
    motivo: 'Motivo',
    quedaAuditoria: 'Queda en Auditoría con tu nombre.',
    cancelar: 'Cancelar',
    admitirBoton: 'Admitir',
    deshacer: 'Deshacer la llegada',
    deshacerDe: (nombre: string) => `Deshacer la llegada de ${nombre}`,
    deshacerSinConexion: 'Deshacer una llegada necesita conexión.',
    deshecha: (motivo: string) => `Llegada deshecha · ${motivo}`,
    evaluar: 'Evaluar',
    evaluacion: 'Evaluación de puerta',
    evaluacionDe: (nombre: string) => `Evaluación de puerta de ${nombre}`,
    notaEvaluacion: 'No escribas datos de salud, ideología, origen ni acusaciones. El cliente puede pedir una copia de estas notas.',
    vigente: (nivel: string, hasta: string) => `Vigente: ${nivel} hasta el ${hasta}`,
    /** La fila «Evaluación de puerta» de la ficha del pase (6.1, `?estado=evaluacion-vigente`). */
    evaluacionVigente: (nivel: string, motivo: string | null, caduca: string) => `Vigente: ${nivel}${motivo === null ? '' : ` · ${motivo}`} · caduca el ${caduca}`,
    evaluacionGuardada: (nivel: string, motivo: string | null, caduca: string | null) =>
        `${nivel}${motivo === null ? '' : ` · ${motivo}`} · ${caduca === null ? 'se ha quitado la que tenía' : `caduca el ${caduca}`}`,
    evaluacionSinVigente: 'Ámbar o rojo con un motivo; verde quita la que tenga.',
    evaluacionSinConexion: 'Necesita conexión.',
    cambiarEvaluacion: 'Cambiar',
    guardar: 'Guardar',
    rapido: 'Rápido',
    detalle: 'Detalle',
    taquilla: 'Taquilla',
    cerrar: 'Cerrar',
    comprobando: 'Comprobando…',
    validando: 'Validando el pase',
    conLaCopia: 'Con la copia de esta tablet',
    acompanantes: (n: number) => `+${String(n)} acompañante${n === 1 ? '' : 's'} en el mismo pase`,
    sinCopia: 'Esta tablet no tiene copia para trabajar sin conexión',
    sinCopiaTexto: 'Descárgala ahora: si se va la conexión, seguirás validando.',
    descargar: 'Descargar la copia',
    sinConexionSinCopia: 'Sin conexión y sin copia en esta tablet',
    sinConexionSinCopiaTexto: 'No se puede validar hasta que vuelva la conexión.',
    sinConexionCopia: (hora: string) => `Sin conexión · validando con la copia de las ${hora}`,
    porEnviar: (n: number) => `${String(n)} llegada${n === 1 ? '' : 's'} por enviar.`,
    ultimaConexion: (cuando: string, hasta: string) => `Última conexión: ${cuando}. Puedes seguir validando hasta ${hasta}.`,
    enviando: 'Enviando…',
    enviadas: (n: number) => `Enviadas ${String(n)} llegada${n === 1 ? '' : 's'}`,
    rechazadas: (n: number) => `${String(n)} lectura${n === 1 ? '' : 's'} rechazada${n === 1 ? '' : 's'}: se hicieron sin una sesión con PIN comprobada antes de perder la conexión. Quedan en Actividad.`,
    conflictos: (n: number) => `${String(n)} en conflicto: las revisa un encargado en Puerta › Conflictos.`,
    entendido: 'Entendido',
    cupoDeLaTablet: 'Parte del cupo de taquilla para esta tablet',
    taquillaTitulo: 'Taquilla de esta tablet',
    cupo: 'Cupo',
    vendidas: 'Vendidas',
    quedan: 'Quedan',
    abrirTaquilla: 'Abrir la taquilla',
    agotada: 'No puede vender más aunque otra tablet tenga cupo.',
    /** G970: si el cliente da su móvil o su email (`contact` de la venta), el pase le llega al volver la conexión. */
    taquillaSinConexion: 'Sin conexión vendes de la parte del cupo de esta tablet. Si el cliente da su móvil o su email, el pase le llega al volver la conexión, cuando se envían las ventas y Eventos crea los pedidos.',
    noEsTuya: NO_ES_TUYA,
    sinGrupo: 'No hay ningún evento ni jornada abierto ahora',
    sinGrupoTexto: 'Puedes validar igualmente: el pase dice si vale ahora.',
    eventoOJornada: 'Evento o jornada',
    pinEncargado: 'Admite un encargado',
    pinEncargadoSub: 'Toca tu nombre y escribe tu PIN. Solo para esta decisión: la tablet sigue con la persona de antes.',
} as const;
